import { useEffect, useState } from 'react'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import { getMyPolls, votePoll } from '../../_data/resident'
import { RenterPoll } from '../../_types/resident'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  PollList,
  PollHeaderRow,
  PollQuestion,
  PollClosedTag,
  OptionList,
  VoteOptionButton,
  ResultRow,
  ResultLabelRow,
  ResultLabel,
  ResultCount,
  ResultBarTrack,
  ResultBarFill,
  PollMeta,
} from './styled'

/**
 * The renter's Polls feed for the selected residence: the PM's polls, newest
 * first. An open poll the renter hasn't voted on shows the options as buttons;
 * once they vote (or the poll closes) it shows the aggregated results as bars
 * with their own pick ticked. One vote each. Keyed on the selected tenancy.
 *
 * @returns {JSX.Element}
 */
const PollsPage = (): JSX.Element => {
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [polls, setPolls] = useState<RenterPoll[]>([])
  const [isLoadingPolls, setIsLoadingPolls] = useState<boolean>(false)
  const [votingPollId, setVotingPollId] = useState<string>('')

  useEffect(() => {
    if (selectedTenancyId === '') {
      setPolls([])
      setIsLoadingPolls(false)
      return
    }

    let isMounted = true
    setPolls([])
    setIsLoadingPolls(true)

    void getMyPolls(selectedTenancyId).then(loaded => {
      if (isMounted === false) {
        return
      }
      setPolls(loaded)
      setIsLoadingPolls(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId])

  const handleVote = (pollId: string, optionId: string): void => {
    if (selectedTenancyId === '') {
      return
    }
    setVotingPollId(pollId)
    void votePoll(selectedTenancyId, pollId, optionId).then(wasRecorded => {
      if (wasRecorded === false) {
        setVotingPollId('')
        return
      }
      // Re-fetch rather than hand-incrementing: the server vote is idempotent,
      // so a stale tab re-voting must reflect the true aggregated tally + the
      // renter's actual stored choice, not a local guess.
      void getMyPolls(selectedTenancyId).then(loaded => {
        setPolls(loaded)
        setVotingPollId('')
      })
    })
  }

  const hasPolls = polls.length > 0

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Polls</PageTitle>
          <PageSubtitle>
            Your property manager wants your input — one vote each.
          </PageSubtitle>
        </PageHeader>

        <TenancySwitcher />

        {isLoadingTenancies === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading…</QuietText>
          </CardSurface>
        ) : null}

        {isLoadingTenancies === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have any tenancies yet. When your property manager
              invites you, claim the invite from your email to see polls here.
            </QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null && isLoadingPolls === true ? (
          <CardSurface padding="loose">
            <QuietText>Loading polls…</QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null &&
        isLoadingPolls === false &&
        hasPolls === false ? (
          <CardSurface padding="loose">
            <QuietText>
              No polls right now. When your property manager runs one, it shows
              up here.
            </QuietText>
          </CardSurface>
        ) : null}

        {hasPolls === true ? (
          <PollList>
            {polls.map(poll => {
              const totalVotes = poll.results.reduce(
                (sum, result) => sum + result.count,
                0,
              )
              const canVote = poll.status === 'open' && poll.myOptionId === null
              return (
                <CardSurface key={poll.id} padding="standard">
                  <PollHeaderRow>
                    <PollQuestion>{poll.question}</PollQuestion>
                    {poll.status === 'closed' ? (
                      <PollClosedTag>Closed</PollClosedTag>
                    ) : null}
                  </PollHeaderRow>

                  {canVote === true ? (
                    <OptionList>
                      {poll.options.map(option => (
                        <VoteOptionButton
                          key={option.id}
                          type="button"
                          disabled={votingPollId === poll.id}
                          onClick={() => handleVote(poll.id, option.id)}
                        >
                          {option.label}
                        </VoteOptionButton>
                      ))}
                    </OptionList>
                  ) : (
                    <OptionList>
                      {poll.options.map(option => {
                        const result = poll.results.find(
                          entry => entry.optionId === option.id,
                        )
                        const count = result?.count ?? 0
                        const percent =
                          totalVotes === 0
                            ? 0
                            : Math.round((count / totalVotes) * 100)
                        const isMine = poll.myOptionId === option.id
                        return (
                          <ResultRow key={option.id}>
                            <ResultLabelRow>
                              <ResultLabel>
                                {option.label}
                                {isMine === true ? ' ✓' : ''}
                              </ResultLabel>
                              <ResultCount>
                                {percent}% · {count}
                              </ResultCount>
                            </ResultLabelRow>
                            <ResultBarTrack>
                              <ResultBarFill style={{ width: `${percent}%` }} />
                            </ResultBarTrack>
                          </ResultRow>
                        )
                      })}
                    </OptionList>
                  )}

                  <PollMeta>
                    {totalVotes === 1 ? '1 vote' : `${totalVotes} votes`}
                    {poll.myOptionId !== null ? ' · You voted' : ''}
                  </PollMeta>
                </CardSurface>
              )
            })}
          </PollList>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default PollsPage
